import React from "react";
import { Box, Stack, Typography } from "@mui/material";

import type {
  L1Dimension,
  L2Method,
  L3Context,
  MaintenancePlan,
  ProgressCriteria,
  PromptHierarchy,
  RatingScalePoint,
  TargetFormValues,
} from "../TargetBuilderPanel.types";
import {
  CHAINING_OPTIONS,
  CRITERIA_OUTCOME_TEXT,
  deriveCriteriaDirection,
  FIRST_TRIAL_TYPE_OPTIONS,
  getNetCountMode,
  getThresholdUiConfigForUnit,
  MASTERY_PROMPT_LEVEL_OPTIONS,
  NET_COUNT_MODE_OPTIONS,
  PROMPT_LEVELS,
  STEP_SCORING_OPTIONS,
  isDiscreteTrialProcedure,
  isFreeOperantProcedure,
  isNETProcedure,
  isTaskAnalysisProcedure,
} from "../TargetBuilderPanel.types";

/**
 * Read-only row blocks rendering a target's measurement design and criteria
 * from the normalized `TargetFormValues` shape. Shared between the builder's
 * live preview (Formik values) and the target detail sidebar (API target via
 * `targetToFormValues`), so both views always surface the same fields.
 */

export const EMPTY = "-";

export const labelFor = <T extends string>(
  value: T | "",
  options: ReadonlyArray<{ value: T; label: string }>
): string => options.find((o) => o.value === value)?.label ?? EMPTY;

export const formatProgress = (
  c: ProgressCriteria | undefined,
  direction: "above" | "below",
  thresholdSuffix: string,
  outcome?: string
): string => {
  // Use explicit empty checks for numeric fields to handle 0 correctly
  if (
    !c ||
    c.consecutive_points === "" ||
    c.consecutive_points == null ||
    c.percent === "" ||
    c.percent == null
  ) {
    return EMPTY;
  }
  // Symbol suffixes (% and /N) attach directly to the number; word suffixes (sec, min) need a space.
  const space = thresholdSuffix === "%" || thresholdSuffix.startsWith("/") ? "" : " ";
  const threshold = `${c.consecutive_points} consecutive data points ${direction} ${c.percent}${space}${thresholdSuffix}`;
  return outcome ? `${threshold} ${outcome}` : threshold;
};

export const formatMaintenance = (m: MaintenancePlan | undefined): string => {
  if (
    !m ||
    m.first_month_every_n_sessions === "" ||
    m.first_month_every_n_sessions == null ||
    m.next_three_months_every_n_sessions === "" ||
    m.next_three_months_every_n_sessions == null
  ) {
    return EMPTY;
  }

  return `Present target every ${m.first_month_every_n_sessions} sessions for first month and every ${m.next_three_months_every_n_sessions} sessions for next 3 months`;
};

// ---------------------------------------------------------------------------
// Short labels for the Measurement & Instruction Design rows
// ---------------------------------------------------------------------------

const L1_SHORT_LABEL: Record<L1Dimension, string> = {
  frequency: "Frequency",
  duration: "Duration",
  latency: "Latency",
  magnitude: "Magnitude",
};

const L2_SHORT_LABEL: Record<L2Method, string> = {
  "frequency-count": "Event Recording",
  "start-stop-recording": "Start/Stop",
  "latency-recording": "Latency Recording",
  "anchored-rating-scale": "Rating Scale",
};

const L3_SHORT_LABEL: Record<L3Context, string> = {
  "discrete-trial": "Discrete Trial",
  net: "Natural Environment (NET)",
  "task-analysis": "Task Analysis",
  "free-operant": "Free Operant",
};

const RATING_FREQUENCY_LABEL: Record<string, string> = {
  daily: "Daily",
  "once-per-session": "Once per session",
  "per-activity": "Per activity",
};

const PROMPT_LABEL_BY_CODE: Record<string, string> = PROMPT_LEVELS.reduce(
  (acc, p) => {
    acc[p.code] = p.label;
    return acc;
  },
  {} as Record<string, string>
);

const masteryPromptLabel = (value: PromptHierarchy["mastery_prompt_level"]): string => {
  if (!value) return EMPTY;
  return MASTERY_PROMPT_LEVEL_OPTIONS.find((o) => o.value === value)?.label ?? EMPTY;
};

/**
 * Returns the prompt_hierarchy of a teaching procedure, or null if the
 * procedure doesn't carry one (e.g. FreeOperant — monitoring only).
 */
const getPromptHierarchy = (
  procedure: TargetFormValues["teaching_procedure"]
): PromptHierarchy | null => {
  if (!procedure) return null;
  if ("prompt_hierarchy" in procedure) return procedure.prompt_hierarchy;
  return null;
};

/**
 * Returns the rating-scale anchors that have a non-empty title — only anchors
 * the clinician has actually filled in are surfaced.
 */
const getFilledAnchors = (config: TargetFormValues["l1_sub_config"]): RatingScalePoint[] => {
  if (!config?.anchors || !Array.isArray(config.anchors)) return [];
  return config.anchors.filter((a) => a.title?.trim().length > 0);
};

/**
 * Returns the intervention strategies attached to the current teaching
 * procedure. Returns an empty array when the procedure doesn't carry the
 * common fields (legacy targets).
 */
const getInterventionStrategies = (procedure: TargetFormValues["teaching_procedure"]): string[] => {
  if (!procedure) return [];
  if ("intervention_strategies" in procedure && Array.isArray(procedure.intervention_strategies)) {
    return procedure.intervention_strategies;
  }
  return [];
};

// ---------------------------------------------------------------------------
// Row primitives
// ---------------------------------------------------------------------------

/**
 * Horizontal row: label on the left, value (text or pill) on the right.
 * Used inside the Target Overview card.
 */
export const SplitRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

/**
 * Vertical row: label on top, value below. Used in Measurement and Mastery cards.
 */
export const StackedRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        whiteSpace: "nowrap",
        color: (theme) => (theme.palette as any).grey?.[600] ?? "#798397",
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      color="text.primary"
      lineHeight="140%"
      sx={{ wordBreak: "break-word", letterSpacing: "0.2px" }}
    >
      {value}
    </Typography>
  </Box>
);

/**
 * StackedRow that only renders when the text value is non-empty — used for
 * optional / conditional form fields so the view grows as data fills in.
 */
export const TextRow: React.FC<{ label: string; value: string }> = ({ label, value }) =>
  value.trim().length > 0 ? <StackedRow label={label} value={value} /> : null;

/**
 * Criteria value: the formatted threshold line(s) plus the clinician's
 * optional notes underneath in secondary color. `secondLine` carries the
 * baseline criteria's second transition (→ generalization) when filled.
 */
const CriteriaValue: React.FC<{ main: string; secondLine?: string; notes?: string }> = ({
  main,
  secondLine,
  notes,
}) => (
  <>
    {main}
    {secondLine && secondLine !== EMPTY && (
      <Typography
        component="span"
        variant="caption"
        color="text.primary"
        sx={{ display: "block", mt: 1, wordBreak: "break-word", letterSpacing: "0.2px" }}
      >
        {secondLine}
      </Typography>
    )}
    {notes && notes.trim().length > 0 && (
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", wordBreak: "break-word" }}
      >
        {notes}
      </Typography>
    )}
  </>
);

export const ValueText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.primary" textAlign="right">
    {children}
  </Typography>
);

/**
 * Single anchor row used inside the "Scale anchors" and "Task steps" blocks.
 * Renders the numeric level (bold, fixed-width) next to the title.
 */
const AnchorRow: React.FC<{ level: number; title: string }> = ({ level, title }) => (
  <Box display="flex" alignItems="baseline" gap={2}>
    <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 12, color: "text.primary" }}>
      {level}
    </Typography>
    <Typography variant="caption" color="text.primary">
      {title}
    </Typography>
  </Box>
);

/**
 * Small badge-style chip used in the Prompt hierarchy, Intervention
 * strategies, and Resources rows.
 */
export const PromptChip: React.FC<{ label: string }> = ({ label }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 2,
      py: "2px",
      bgcolor: (theme) => (theme.palette as any).grey?.[100] ?? "#EAECEF",
      color: "text.primary",
      borderRadius: "6px",
      fontSize: 12,
      lineHeight: 1.4,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

/**
 * Resources rendered as a row of chips, falling back to the URL for untitled
 * links, or the empty placeholder when there are none.
 */
export const ResourcesRow: React.FC<{ resources: TargetFormValues["resources"] }> = ({
  resources,
}) => (
  <StackedRow
    label="Resources"
    value={
      resources.length > 0 ? (
        <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
          {resources.map((resource, index) => (
            <PromptChip key={`${resource.url}-${index}`} label={resource.title || resource.url} />
          ))}
        </Stack>
      ) : (
        EMPTY
      )
    }
  />
);

// ---------------------------------------------------------------------------
// Measurement & instruction design rows
// ---------------------------------------------------------------------------

/**
 * Rows mirror the form's field order: L1 → L2 (+ rating-scale config) → L3 →
 * teaching procedure (common fields first, then type-specific fields in their
 * on-form positions). Conditional fields only render when filled.
 */
export const TargetMeasurementRows: React.FC<{
  values: TargetFormValues;
  /**
   * The goal's behavior subject. Lives outside `TargetFormValues` (it's a
   * goal-level field), but the NET count-type selector is hidden in the form
   * for caregiver goals — passed through so the preview doesn't surface a
   * read-only row for a field the clinician never had a chance to set.
   */
  behaviorSubject?: "client" | "caregiver";
}> = ({ values, behaviorSubject = "client" }) => {
  const dimensionLabel = values.l1_dimension
    ? L1_SHORT_LABEL[values.l1_dimension as L1Dimension] ?? EMPTY
    : EMPTY;
  const methodLabel = values.l2_method
    ? L2_SHORT_LABEL[values.l2_method as L2Method] ?? EMPTY
    : EMPTY;
  const contextLabel = values.l3_context
    ? L3_SHORT_LABEL[values.l3_context as L3Context] ?? EMPTY
    : EMPTY;

  const procedure = values.teaching_procedure;
  const promptHierarchy = getPromptHierarchy(procedure);
  const promptLevels = promptHierarchy?.levels ?? [];

  // Rating-scale targets don't use prompts — surface the anchors the clinician
  // defined instead so the rows reflect what's actually being measured.
  const isRatingScale = values.l2_method === "anchored-rating-scale";
  const filledAnchors = isRatingScale ? getFilledAnchors(values.l1_sub_config) : [];
  const ratingFrequencyLabel =
    isRatingScale && values.l1_sub_config?.rating_frequency
      ? RATING_FREQUENCY_LABEL[values.l1_sub_config.rating_frequency] ?? ""
      : "";

  const interventionStrategies = getInterventionStrategies(procedure);

  // Task-analysis steps the clinician actually typed.
  const filledSteps = isTaskAnalysisProcedure(procedure)
    ? procedure.steps.filter((s) => s.trim().length > 0)
    : [];

  // Prompt-hierarchy rows — inserted inline where the form renders the editor
  // (DT: after Correct response; NET: after Opportunity definition). Hidden
  // whenever the form hides the editor (TeachingProcedureSection's rules):
  // rating-scale targets, caregiver goals (coaching config replaces prompts),
  // behavior-reduction targets, and task-analysis targets scored binary.
  const isBinaryTaskAnalysis =
    isTaskAnalysisProcedure(procedure) && procedure.step_scoring !== "prompt-hierarchy";
  const hidePromptHierarchy =
    isRatingScale || behaviorSubject === "caregiver" || values.target_type === "behavior-reduction";

  // NET count mode — frequency/latency NET targets only use the
  // prompt hierarchy when the clinician selected the "prompt-hierarchy" count
  // type; the Count type row itself renders inside the NET block below.
  // Mirrors the form's `hasCountModeSelector` gating in NETProcedure.tsx: the
  // selector (and thus this read-only row) never renders for caregiver goals
  // or behavior-reduction targets, so the preview must stay in sync — showing
  // it anyway would surface a value the clinician never had a chance to set.
  const hasNetCountMode =
    isNETProcedure(procedure) &&
    (values.l1_dimension === "frequency" || values.l1_dimension === "latency") &&
    behaviorSubject !== "caregiver" &&
    values.target_type !== "behavior-reduction";
  const netCountMode = hasNetCountMode
    ? getNetCountMode(procedure, values.l1_dimension, values.target_type)
    : null;
  const isNonPromptNet = hasNetCountMode && netCountMode !== "prompt-hierarchy";

  const showPromptRows =
    !hidePromptHierarchy && !isBinaryTaskAnalysis && !isNonPromptNet && promptHierarchy;
  const promptRows = showPromptRows ? (
    <>
      <StackedRow
        label="Prompt hierarchy"
        value={
          promptLevels.length > 0 ? (
            <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
              {promptLevels.map((code) => (
                <PromptChip key={code} label={PROMPT_LABEL_BY_CODE[code] ?? code} />
              ))}
            </Stack>
          ) : (
            EMPTY
          )
        }
      />
      <StackedRow
        label="Mastery prompt level"
        value={masteryPromptLabel(promptHierarchy.mastery_prompt_level)}
      />
    </>
  ) : null;

  // "Count prompted as correct" only has a real editable control in the
  // Discrete Trial form (CountPromptedSection) — NET and Task Analysis never
  // render that toggle, so showing this row for them would surface a value
  // the clinician never had a chance to set. Scope it to DT only.
  const countPromptedAsCorrectRow =
    showPromptRows && isDiscreteTrialProcedure(procedure) ? (
      <StackedRow
        label="Count prompted as correct"
        value={promptHierarchy.count_prompted_as_correct ? "Yes" : "No"}
      />
    ) : null;

  return (
    <>
      <StackedRow label="Dimension" value={dimensionLabel} />
      <StackedRow label="Method" value={methodLabel} />

      {/* Rating-scale config renders between the L2 and L3 selectors. */}
      {ratingFrequencyLabel && <StackedRow label="Rating frequency" value={ratingFrequencyLabel} />}
      {isRatingScale && filledAnchors.length > 0 && (
        <StackedRow
          label="Scale anchors"
          value={
            <Stack gap={1} mt={1}>
              {filledAnchors.map((anchor) => (
                <AnchorRow key={anchor.level} level={anchor.level} title={anchor.title} />
              ))}
            </Stack>
          }
        />
      )}

      <StackedRow label="Context" value={contextLabel} />

      {/* Common teaching-procedure fields open the procedure block in the
          form. Optional chaining guards legacy targets created before these
          fields existed. */}
      {interventionStrategies.length > 0 && (
        <StackedRow
          label="Intervention strategies"
          value={
            <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
              {interventionStrategies.map((strategy, index) => (
                <PromptChip key={`${strategy}-${index}`} label={strategy} />
              ))}
            </Stack>
          }
        />
      )}
      <TextRow
        label="Intervention instructions"
        value={procedure?.intervention_instructions ?? ""}
      />
      <TextRow label="Generalization plan" value={procedure?.generalization_plan ?? ""} />
      <TextRow label="Date of implementation" value={procedure?.date_of_implementation ?? ""} />
      <TextRow
        label="Function"
        value={
          typeof procedure?.function === "string" && procedure.function.length > 0
            ? procedure.function.charAt(0).toUpperCase() + procedure.function.slice(1)
            : ""
        }
      />
      <TextRow label="Assessment used" value={procedure?.assessment_used ?? ""} />
      <TextRow label="Common antecedents" value={procedure?.common_antecedents ?? ""} />
      <TextRow label="Antecedent strategies" value={procedure?.antecedent_strategies ?? ""} />
      <TextRow
        label="Linked replacement behavior"
        value={
          procedure?.linked_replacement_behavior == null ||
          procedure?.linked_replacement_behavior === ""
            ? ""
            : String(procedure.linked_replacement_behavior)
        }
      />
      <TextRow label="Safety considerations" value={procedure?.safety_considerations ?? ""} />
      <TextRow label="Modeling" value={procedure?.coaching_modeling_notes ?? ""} />
      <TextRow label="Rehearsal" value={procedure?.coaching_rehearsal_notes ?? ""} />
      <TextRow label="Feedback" value={procedure?.coaching_feedback_notes ?? ""} />
      <TextRow label="Visual supports" value={procedure?.coaching_visual_supports_notes ?? ""} />
      <TextRow
        label="Error correction procedure"
        value={procedure?.error_correction_procedure ?? ""}
      />
      <TextRow
        label="Practice opportunities per session"
        value={procedure?.practice_opportunities_per_session ?? ""}
      />

      {isDiscreteTrialProcedure(procedure) && (
        <>
          <TextRow label="Discriminative stimulus (SD)" value={procedure.sd_wording} />
          <TextRow label="Correct response" value={procedure.correct_response} />
          {promptRows}
          <TextRow label="Error correction" value={procedure.error_correction} />
          <TextRow
            label="Teaching opportunities per session"
            value={procedure.teaching_opportunities_per_session ?? ""}
          />
          <TextRow
            label="First trial type"
            value={
              procedure.first_trial_type
                ? labelFor(procedure.first_trial_type, FIRST_TRIAL_TYPE_OPTIONS)
                : ""
            }
          />
          {countPromptedAsCorrectRow}
        </>
      )}

      {isNETProcedure(procedure) && (
        <>
          <TextRow label="Opportunity definition" value={procedure.opportunity_definition} />
          {netCountMode && (
            <TextRow label="Count type" value={labelFor(netCountMode, NET_COUNT_MODE_OPTIONS)} />
          )}
          {promptRows}
          <TextRow label="Reinforcement notes" value={procedure.reinforcement_notes} />
          <TextRow
            label="Teaching opportunities per session"
            value={procedure.teaching_opportunities_per_session ?? ""}
          />
        </>
      )}

      {/* Task analysis only surfaces the prompt-hierarchy editor (and
          promptRows below) when step_scoring is "prompt-hierarchy" — see
          isBinaryTaskAnalysis above, mirroring the form's own gating. */}
      {isTaskAnalysisProcedure(procedure) && (
        <>
          {filledSteps.length > 0 && (
            <StackedRow
              label="Task steps"
              value={
                <Stack gap={1} mt={1}>
                  {filledSteps.map((step, index) => (
                    <AnchorRow key={`${step}-${index}`} level={index + 1} title={step} />
                  ))}
                </Stack>
              }
            />
          )}
          <TextRow
            label="Chaining"
            value={procedure.chaining ? labelFor(procedure.chaining, CHAINING_OPTIONS) : ""}
          />
          <TextRow
            label="Step scoring"
            value={
              procedure.step_scoring ? labelFor(procedure.step_scoring, STEP_SCORING_OPTIONS) : ""
            }
          />
          {promptRows}
          <TextRow
            label="Teaching opportunities per session"
            value={procedure.teaching_opportunities_per_session ?? ""}
          />
          <TextRow label="Step advancement criteria" value={procedure.step_advancement_criteria} />
        </>
      )}

      {isFreeOperantProcedure(procedure) && (
        <>
          <TextRow label="Operational definition" value={procedure.operational_definition} />
          <TextRow label="Typical contexts" value={procedure.typical_contexts} />
          <TextRow label="Antecedent conditions" value={procedure.antecedent_conditions} />
          <TextRow
            label="Response / consequence notes"
            value={procedure.response_consequence_notes}
          />
        </>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Mastery criteria rows
// ---------------------------------------------------------------------------

/**
 * The five phase-transition criteria in form order, each with its optional
 * clinician notes underneath.
 */
export const TargetCriteriaRows: React.FC<{ values: TargetFormValues }> = ({ values }) =>
  (() => {
    const baselineInterventionUi = getThresholdUiConfigForUnit(
      values.l1_dimension,
      values.baseline_progression_criteria?.to_intervention?.threshold_unit,
      values.l3_context,
      values.teaching_procedure,
      values.target_type
    );
    const baselineGeneralizationUi = getThresholdUiConfigForUnit(
      values.l1_dimension,
      values.baseline_progression_criteria?.to_generalization?.threshold_unit,
      values.l3_context,
      values.teaching_procedure,
      values.target_type
    );
    const masteryUi = getThresholdUiConfigForUnit(
      values.l1_dimension,
      values.mastery_criteria?.threshold_unit,
      values.l3_context,
      values.teaching_procedure,
      values.target_type
    );
    const regressionUi = getThresholdUiConfigForUnit(
      values.l1_dimension,
      values.regression_criteria?.threshold_unit,
      values.l3_context,
      values.teaching_procedure,
      values.target_type
    );
    const generalizationUi = getThresholdUiConfigForUnit(
      values.l1_dimension,
      values.generalization_criteria?.threshold_unit,
      values.l3_context,
      values.teaching_procedure,
      values.target_type
    );
    const baselineToInterventionDirection = deriveCriteriaDirection(
      values.l1_dimension,
      values.target_type,
      "baseline.to_intervention"
    );
    const baselineToGeneralizationDirection = deriveCriteriaDirection(
      values.l1_dimension,
      values.target_type,
      "baseline.to_generalization"
    );
    const masteryDirection = deriveCriteriaDirection(
      values.l1_dimension,
      values.target_type,
      "mastery"
    );
    const regressionDirection = deriveCriteriaDirection(
      values.l1_dimension,
      values.target_type,
      "regression"
    );
    const generalizationDirection = deriveCriteriaDirection(
      values.l1_dimension,
      values.target_type,
      "generalization"
    );

    return (
      <>
        {/* Baseline notes are shared between both sub-criteria (kept in sync by
        the form), so they render once under the pair of transition lines. */}
        <StackedRow
          label="Baseline progression criteria"
          value={
            <CriteriaValue
              main={formatProgress(
                values.baseline_progression_criteria?.to_intervention,
                baselineToInterventionDirection,
                baselineInterventionUi.suffix,
                CRITERIA_OUTCOME_TEXT.baselineToIntervention
              )}
              secondLine={formatProgress(
                values.baseline_progression_criteria?.to_generalization,
                baselineToGeneralizationDirection,
                baselineGeneralizationUi.suffix,
                CRITERIA_OUTCOME_TEXT.baselineToGeneralization
              )}
              notes={values.baseline_progression_criteria?.to_intervention?.notes}
            />
          }
        />
        <StackedRow
          label="Mastery criteria"
          value={
            <CriteriaValue
              main={formatProgress(
                values.mastery_criteria,
                masteryDirection,
                masteryUi.suffix,
                CRITERIA_OUTCOME_TEXT.mastery
              )}
              notes={values.mastery_criteria?.notes}
            />
          }
        />
        <StackedRow
          label="Regression criteria"
          value={
            <CriteriaValue
              main={formatProgress(
                values.regression_criteria,
                regressionDirection,
                regressionUi.suffix,
                CRITERIA_OUTCOME_TEXT.regression
              )}
              notes={values.regression_criteria?.notes}
            />
          }
        />
        <StackedRow
          label="Generalization criteria"
          value={
            <CriteriaValue
              main={formatProgress(
                values.generalization_criteria,
                generalizationDirection,
                generalizationUi.suffix,
                CRITERIA_OUTCOME_TEXT.generalization
              )}
              notes={values.generalization_criteria?.notes}
            />
          }
        />
        <StackedRow
          label="Maintenance criteria"
          value={
            <CriteriaValue
              main={formatMaintenance(values.maintenance_plan)}
              notes={values.maintenance_plan?.notes}
            />
          }
        />
      </>
    );
  })();
